/**
 * MXlab — Véhicule mémorisé : sélecteur, pastille, stockage.
 *
 * Trois éléments personnalisés, un seul module :
 *   <mx-vehicle-picker>  la cascade à quatre champs (sections/mx-vehicle-picker.liquid)
 *   <mx-vehicle-chip>    la pastille du véhicule mémorisé (snippets/mx-vehicle-chip.liquid)
 *   <mx-vehicle-drawer>  le tiroir qui les récupère à la demande
 *                        (snippets/mx-vehicle-drawer.liquid, toujours rendu en pied de
 *                        page) — la pastille et le bandeau de compatibilité l'ouvrent tous
 *                        deux par le même événement `mx:vehicle-open`, sans savoir qu'il
 *                        existe.
 *
 * Le module est chargé par la section DICTIONNAIRE (présente sur toutes les pages) ET par
 * la section SÉLECTEUR. Aucun risque de double exécution : un module ES est indexé par son
 * URL et n'est évalué qu'une fois par document. Le sélecteur le charge quand même, pour
 * rester autonome si le dictionnaire venait à être retiré du pied de page.
 *
 * Les fonctions partagées (localStorage gardé, nettoyage d'URL) sont DUPLIQUÉES depuis
 * mx-customlab.js, à dessein : aucune entrée n'est ajoutée à l'import map `@theme/*` de
 * snippets/scripts.liquid, qui est un fichier natif (règle 1 de CLAUDE.md).
 *
 * ── CE QUI EST MÉMORISÉ : UN HANDLE, ET RIEN D'AUTRE.
 * Seule valeur qui traverse les langues intacte. Un libellé stocké serait figé dans la
 * langue où il a été choisi et deviendrait faux au premier changement de langue — or la
 * majorité du trafic n'est pas francophone. Le libellé se relit à chaque page dans le
 * dictionnaire, rendu par Liquid, donc toujours dans la langue courante.
 */

/* La clé et le numéro de schéma sont des LITTÉRAUX, jamais des réglages. Un réglage
   `type: "text"` matérialisé dans une template JSON devient traduisible par Translate &
   Adapt : la clé serait traduite sur les parcours non francophones et le véhicule
   deviendrait introuvable. Même arbitrage que `_custom-model-id` dans mx-customlab.js.

   ⚠️⚠️ CES DEUX LIGNES SONT DUPLIQUÉES DANS `assets/mx-compat.js`, ET LES DEUX MODULES
   LISENT LE MÊME STOCKAGE. La duplication est voulue — aucune entrée ajoutée à l'import
   map native (règle 1 de CLAUDE.md) — mais rien dans le code ne force à les changer
   ensemble. Bumper `SCHEMA` d'un seul côté ne casse RIEN de visible : le module resté en
   arrière juge simplement le contenu invalide et rend `null`, donc le véhicule disparaît
   pour lui seul, sans erreur ni journal. Toute modification de `STORE_KEY`, de `SCHEMA`
   ou de la FORME de l'objet stocké (`{ gabarit, v }`) se répercute dans
   `assets/mx-compat.js`, au même endroit, dans le même commit. */
const STORE_KEY = 'mxlab_vehicule';
const SCHEMA = 1;
const EVENT = 'mx:vehicle-changed';
/* Demande d'ouverture VENUE D'AILLEURS (le bandeau de compatibilité du lot 2, état 1).
   Un événement plutôt qu'une méthode publique : le demandeur n'a alors ni à trouver la
   pastille, ni à savoir qu'elle existe — s'il n'y en a pas sur la page, il n'y a personne
   pour écouter et son propre repli (un lien vers le sélecteur) s'applique. */
const OPEN_EVENT = 'mx:vehicle-open';
const PARAM = 'vehicule';
const SECTION = 'mx-vehicle-picker';

/* ------------------------------------------------------------------ dictionnaire */

let dictCache;

/**
 * Le dictionnaire, rendu par `mx-vehicle-lookup` dans le pied de page. Lu une fois.
 * Absent (section retirée) => tout le module se met en retrait : le sélecteur reste
 * masqué et la pastille ne s'affiche pas. Un sélecteur sans mots ne sert à personne.
 */
function dict() {
  if (dictCache !== undefined) return dictCache;
  dictCache = null;
  const el = document.querySelector('script[type="application/json"][data-mx-vehicle-lookup]');
  if (el) {
    try {
      const o = JSON.parse(el.textContent);
      if (o && o.gabarits) dictCache = o;
    } catch (e) {
      /* JSON illisible : on dégrade comme si le dictionnaire était absent */
    }
  }
  return dictCache;
}

function gabaritConnu(handle) {
  const d = dict();
  return !!(handle && d && Object.prototype.hasOwnProperty.call(d.gabarits, handle));
}

function textes() {
  const d = dict();
  return (d && d.t) || {};
}

/* -------------------------------------------------------------------- stockage */

function lsGet(k) {
  try {
    return window.localStorage.getItem(k);
  } catch (e) {
    return null; // navigation privée / stockage refusé : on continue sans persistance
  }
}
function lsSet(k, v) {
  try {
    window.localStorage.setItem(k, v);
  } catch (e) {}
}
function lsRemove(k) {
  try {
    window.localStorage.removeItem(k);
  } catch (e) {}
}

/**
 * Lit le véhicule mémorisé. Renvoie un handle, ou null.
 *
 * `v` est un numéro de SCHÉMA, pas une version d'application : absent ou différent, on
 * IGNORE et on repart de zéro plutôt que de tenter une migration. Une migration devinée
 * sur une forme qu'on ne connaît pas produirait un état à moitié juste, plus difficile à
 * diagnostiquer qu'une remise à zéro.
 */
function lireStockage() {
  const brut = lsGet(STORE_KEY);
  if (!brut) return null;
  let o = null;
  try {
    o = JSON.parse(brut);
  } catch (e) {
    return null;
  }
  if (!o || o.v !== SCHEMA || typeof o.gabarit !== 'string' || !o.gabarit) return null;
  return o.gabarit;
}

function ecrireStockage(handle) {
  lsSet(STORE_KEY, JSON.stringify({ gabarit: handle, v: SCHEMA }));
}

/* ------------------------------------------------------------------ état courant */

let courant = null;

/**
 * Change le véhicule et prévient tout le monde.
 * Le détail ne porte QUE le handle : les blocs abonnés résolvent eux-mêmes ce dont ils
 * ont besoin dans le dictionnaire. Aucun couplage direct entre le sélecteur et la fiche
 * produit.
 */
function definir(handle) {
  const valide = gabaritConnu(handle) ? handle : null;
  if (valide === courant) return;
  courant = valide;
  if (valide) ecrireStockage(valide);
  else lsRemove(STORE_KEY);
  emettre();
}

function emettre() {
  document.dispatchEvent(new CustomEvent(EVENT, { detail: { gabarit: courant } }));
}

/**
 * Retire le paramètre de préremplissage de l'URL, comme le pont Custom Lab le fait pour
 * son identifiant de configuration : une URL partagée ne doit pas imposer au destinataire
 * le véhicule de celui qui l'a copiée.
 */
function nettoyerUrl() {
  try {
    const url = new URL(window.location.href);
    url.searchParams.delete(PARAM);
    window.history.replaceState({}, document.title, url.pathname + url.search + url.hash);
  } catch (e) {
    /* replaceState indisponible : sans gravité, l'URL garde juste le paramètre */
  }
}

/**
 * Amorçage. Priorité au paramètre d'URL, puis au stockage.
 *
 * Un handle INCONNU — venu de l'URL comme du stockage — est ignoré SANS ERREUR : un
 * gabarit supprimé côté admin doit produire un oubli silencieux, pas une pastille cassée.
 */
function amorcer() {
  let depuisUrl = null;
  try {
    depuisUrl = new URLSearchParams(window.location.search).get(PARAM);
  } catch (e) {
    /* URL illisible : on se rabat sur le stockage */
  }

  if (depuisUrl) {
    // Le paramètre est retiré qu'il soit valide ou non : dans les deux cas il a été traité.
    nettoyerUrl();
    if (gabaritConnu(depuisUrl)) {
      courant = depuisUrl;
      ecrireStockage(depuisUrl);
      return;
    }
  }

  const stocke = lireStockage();
  if (stocke && gabaritConnu(stocke)) {
    courant = stocke;
  } else if (stocke) {
    lsRemove(STORE_KEY); // gabarit disparu du référentiel : oubli silencieux
  }
}

/* ------------------------------------------------------------------- utilitaires */

function vider(el) {
  while (el.firstChild) el.removeChild(el.firstChild);
}

/** Compare deux libellés dans la langue du visiteur, sans nommer de locale. */
function parLibelle(a, b) {
  return String(a).localeCompare(String(b));
}

/* ==================================================================== SÉLECTEUR */

/*
 * ⚠️ AUCUN CHAMP D'INSTANCE NE PORTE UN NOM RÉFLÉCHI PAR LE DOM.
 * `slot` et `form` en sont — comme `hidden`, `title`, `id`, `value`. Écrire
 * `this.slot = unElement` ne range pas l'élément : ça pose l'ATTRIBUT `slot` et la
 * relecture rend une CHAÎNE. Le symptôme est distant et trompeur — ici le tiroir se
 * remplissait correctement puis tombait dans son message d'échec, parce que
 * `this.slot.appendChild` n'existait pas sur une chaîne, et le `catch` avalait tout.
 * D'où `this.zone` et `this.zoneForm`. Même piège que `this.hidden`, déjà documenté
 * dans `.claude/rules/javascript.md`.
 */
class MxVehiclePicker extends HTMLElement {
  connectedCallback() {
    // Le tiroir insère un sélecteur importé d'un autre document ; une ré-insertion ne
    // doit pas recâbler l'élément ni reconstruire ses listes.
    if (this.pret) return;

    const d = dict();
    if (!d) return; // pas de dictionnaire => aucun mot à afficher => on reste masqué

    const script = this.querySelector('script[type="application/json"][data-mx-vehicle-data]');
    if (!script) return;
    try {
      this.data = JSON.parse(script.textContent);
    } catch (e) {
      return;
    }
    if (!this.data || !Array.isArray(this.data.modeles) || !this.data.modeles.length) return;

    this.t = textes();
    this.zoneForm = this.querySelector('[data-mx-form]');
    this.done = this.querySelector('[data-mx-done]');
    this.tiles = this.querySelector('[data-mx-tiles]');
    this.valider = this.querySelector('[data-mx-valider]');
    this.oublier = this.querySelector('[data-mx-oublier]');
    this.selects = {
      univers: this.querySelector('[data-mx-select="univers"]'),
      marque: this.querySelector('[data-mx-select="marque"]'),
      modele: this.querySelector('[data-mx-select="modele"]'),
    };
    this.etapes = {
      univers: this.querySelector('[data-mx-step="univers"]'),
      marque: this.querySelector('[data-mx-step="marque"]'),
      modele: this.querySelector('[data-mx-step="modele"]'),
      gabarit: this.querySelector('[data-mx-step="gabarit"]'),
    };

    this.onSync = this.onSync.bind(this);

    this.poserTextes();
    this.remplirUnivers();
    this.cabler();

    this.pret = true;
    this.hidden = false;
    this.peindre();

    document.addEventListener(EVENT, this.onSync);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onSync);
  }

  /** Tous les mots viennent du dictionnaire, jamais d'un réglage de cette section. */
  poserTextes() {
    this.querySelectorAll('[data-mx-t]').forEach((el) => {
      const cle = el.getAttribute('data-mx-t');
      if (this.t[cle]) el.textContent = this.t[cle];
    });
  }

  /* ------------------------------------------------------------- listes dérivées */

  /**
   * Les univers RÉELLEMENT présents dans le référentiel, dans l'ordre du dictionnaire.
   * Un univers absent du dictionnaire n'est pas perdu : il passe après les autres et
   * s'affiche avec son CODE BRUT, conformément au contrat de robustesse.
   */
  listeUnivers() {
    const d = dict();
    const declares = (d.univers || []).map((u) => u.code);
    const libelles = new Map((d.univers || []).map((u) => [u.code, u.label]));
    const codes = [...new Set(this.data.modeles.map((m) => m.univers))];

    codes.sort((a, b) => {
      const ia = declares.indexOf(a);
      const ib = declares.indexOf(b);
      if (ia === -1 && ib === -1) return parLibelle(a, b);
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });

    return codes.map((code) => ({ code, label: libelles.get(code) || code }));
  }

  listeMarques(univers) {
    const marques = [
      ...new Set(this.data.modeles.filter((m) => m.univers === univers).map((m) => m.marque)),
    ];
    return marques.sort(parLibelle);
  }

  /**
   * Tri par `ordre` croissant PUIS par libellé. Un `ordre` vide sort en dernier : il est
   * sérialisé en `null`, traité ici comme l'infini. Un 0 le ferait remonter en tête.
   */
  listeModeles(univers, marque) {
    return this.data.modeles
      .filter((m) => m.univers === univers && m.marque === marque)
      .slice()
      .sort((a, b) => {
        const oa = a.ordre === null || a.ordre === undefined ? Infinity : a.ordre;
        const ob = b.ordre === null || b.ordre === undefined ? Infinity : b.ordre;
        if (oa !== ob) return oa - ob;
        return parLibelle(a.label, b.label);
      });
  }

  modeleParHandle(handle) {
    return this.data.modeles.find((m) => m.handle === handle) || null;
  }

  modeleDuGabarit(handle) {
    return this.data.modeles.find((m) => m.gabarits.some((g) => g.handle === handle)) || null;
  }

  /** Le gabarit lui-même (silhouette, nom) — le référentiel complet, jamais le
   * dictionnaire, qui n'en porte pas l'image (voir mx-vehicle-lookup.liquid). */
  gabaritParHandle(handle) {
    const modele = this.modeleDuGabarit(handle);
    return modele ? modele.gabarits.find((g) => g.handle === handle) || null : null;
  }

  /* ----------------------------------------------------------------- remplissage */

  option(valeur, libelle) {
    const o = document.createElement('option');
    o.value = valeur;
    o.textContent = libelle;
    return o;
  }

  remplir(select, entrees) {
    vider(select);
    select.appendChild(this.option('', this.t.choisir || ''));
    entrees.forEach((e) => select.appendChild(this.option(e.value, e.label)));
    select.value = '';
  }

  remplirUnivers() {
    this.remplir(
      this.selects.univers,
      this.listeUnivers().map((u) => ({ value: u.code, label: u.label }))
    );
  }

  cabler() {
    this.selects.univers.addEventListener('change', () => {
      this.reinitialiser('marque');
      if (!this.selects.univers.value) return;
      this.remplir(
        this.selects.marque,
        this.listeMarques(this.selects.univers.value).map((m) => ({ value: m, label: m }))
      );
      this.etapes.marque.hidden = false;
    });

    this.selects.marque.addEventListener('change', () => {
      this.reinitialiser('modele');
      if (!this.selects.marque.value) return;
      const modeles = this.listeModeles(this.selects.univers.value, this.selects.marque.value);
      this.remplir(
        this.selects.modele,
        modeles.map((m) => ({ value: m.handle, label: m.label }))
      );
      this.etapes.modele.hidden = false;
    });

    this.selects.modele.addEventListener('change', () => {
      this.reinitialiser('gabarit');
      if (!this.selects.modele.value) return;
      this.remplirGabarits(this.modeleParHandle(this.selects.modele.value));
    });

    const reset = this.querySelector('[data-mx-reset]');
    if (reset) {
      reset.addEventListener('click', () => {
        // « Changer » ROUVRE au véhicule actuel sans effacer le stockage : tant que le
        // client n'a pas validé autre chose, son véhicule reste celui d'avant. L'oubli
        // n'existe pas, il n'y a aucune commande pour ça.
        this.rouvrir();
      });
    }

    if (this.valider) {
      this.valider.addEventListener('click', () => this.validerChoix());
    }

    if (this.oublier) {
      this.oublier.addEventListener('click', () => this.oublierMachine());
    }
  }

  /** Changer un champ amont réinitialise TOUT l'aval, jamais une partie. */
  reinitialiser(depuis) {
    const ordre = ['marque', 'modele', 'gabarit'];
    ordre.slice(ordre.indexOf(depuis)).forEach((cle) => {
      this.etapes[cle].hidden = true;
      if (this.selects[cle]) {
        vider(this.selects[cle]);
        this.selects[cle].value = '';
      }
    });
    vider(this.tiles);
    // Un champ amont a changé : toute sélection en attente ne tient plus.
    this.marquer(null);
  }

  /**
   * @param {object} modele
   * @param {string} [preselect] - Handle à marquer d'emblée comme sélection EN ATTENTE
   *   (rouverture sur un véhicule déjà mémorisé). Sans lui, un modèle à gabarit UNIQUE se
   *   présélectionne tout seul — il n'y a rien à arbitrer — mais reste une sélection en
   *   attente comme les autres : rien n'est écrit tant que « Valider » n'a pas été cliqué.
   */
  remplirGabarits(modele, preselect) {
    if (!modele) return;
    vider(this.tiles);
    const nom = this.tiles.getAttribute('data-mx-name');

    modele.gabarits.forEach((g) => this.tiles.appendChild(this.vignette(g, nom)));
    this.etapes.gabarit.hidden = false;

    const cible = preselect || (modele.gabarits.length === 1 ? modele.gabarits[0].handle : null);
    if (!cible) {
      this.marquer(null);
      return;
    }
    const index = modele.gabarits.findIndex((g) => g.handle === cible);
    const input = index > -1 ? this.tiles.querySelectorAll('input')[index] : null;
    if (input) input.checked = true;
    this.marquer(cible);
  }

  /**
   * ⚠️ `loading="lazy"` NE FONCTIONNE PAS DANS UN `<dialog>` MODAL — mesuré sur Chrome :
   * une image paresseuse posée dans le tiroir reste `complete: false`, `naturalWidth: 0`,
   * INDÉFINIMENT, alors qu'elle est bien dans le cadre visible. Le tiroir affichait donc
   * six cadres vides, sans erreur et sans qu'aucune image ne soit cassée — précisément le
   * mode de défaillance silencieux qu'on cherche à éviter. Basculer deux images sur
   * `eager` les fait charger immédiatement, la voisine restée en `lazy` non : c'est bien
   * la couche supérieure du `<dialog>`, pas le réseau.
   *
   * D'où la stratégie : paresseuse quand le sélecteur est posé DANS LA PAGE (une grille
   * longue sous la ligne de flottaison, c'est là que la paresse sert), immédiate quand il
   * est DANS LE TIROIR — où la grille est exactement ce que le client vient de demander,
   * et où la paresse n'apporterait rien même si elle marchait.
   *
   * `closest('dialog')` est une question posée au DOM, pas un sélecteur de thème écrit en
   * dur : le jour où le tiroir change de forme, la règle suit toute seule.
   */
  chargementImage() {
    return this.closest('dialog') ? 'eager' : 'lazy';
  }

  /**
   * Clone le placeholder (voir `<template data-mx-placeholder>`) dans un conteneur donné.
   * Une seule définition SVG, réutilisée pour toute silhouette absente ou en échec — dans
   * la galerie comme dans l'état validé (`montrerRecap`), tous univers confondus.
   */
  poserPlaceholder(conteneur) {
    const tpl = this.querySelector('template[data-mx-placeholder]');
    if (!tpl) return;
    vider(conteneur);
    conteneur.appendChild(tpl.content.firstElementChild.cloneNode(true));
  }

  /**
   * Une vignette = un vrai `input[type=radio]` dans un `<label>` : la navigation clavier
   * et l'annonce du groupe restent natives, rien n'est réimplémenté.
   *
   * Sans silhouette (dix-neuf gabarits n'en ont pas), le cadre média est TOUJOURS rendu :
   * un cadre vide serait le trou visuel que le contrat interdit, mais l'omettre dirait
   * « il n'y a rien » plutôt que « on n'a pas encore la photo ». Le placeholder (icône
   * `motorbike`, cf. `poserPlaceholder()`) comble ce cadre à la place d'une vraie
   * silhouette.
   */
  vignette(g, nom) {
    const label = document.createElement('label');
    // Voir `chargementImage()` : la stratégie dépend d'où le sélecteur est rendu.
    const chargement = this.chargementImage();
    label.className = 'mx-vpt';

    const input = document.createElement('input');
    input.type = 'radio';
    input.name = nom;
    input.value = g.handle;
    input.className = 'mx-vpt__input';
    input.addEventListener('change', () => {
      if (!input.checked) return;
      this.marquer(g.handle);
    });
    label.appendChild(input);

    const media = document.createElement('span');
    media.className = 'mx-vpt__media';

    if (g.image) {
      const img = document.createElement('img');
      img.className = 'mx-vpt__img';
      img.src = g.image;
      img.alt = g.label; // `alt` égal au libellé du gabarit
      img.loading = chargement;
      img.decoding = 'async';
      // Le bloc doit rester utilisable si l'image ne charge pas : le placeholder prend sa
      // place plutôt que de laisser une icône d'image cassée.
      img.addEventListener('error', () => this.poserPlaceholder(media), { once: true });
      media.appendChild(img);
    } else {
      this.poserPlaceholder(media);
    }
    label.appendChild(media);

    const corps = document.createElement('span');
    corps.className = 'mx-vpt__corps';

    const titre = document.createElement('span');
    titre.className = 'mx-vpt__nom';
    titre.textContent = g.label;
    corps.appendChild(titre);

    // Note absente : aucun conteneur n'est créé, pas même vide.
    if (g.note) {
      const note = document.createElement('span');
      note.className = 'mx-vpt__note';
      note.textContent = g.note;
      corps.appendChild(note);
    }

    label.appendChild(corps);

    return label;
  }

  /* ---------------------------------------------------------------------- états */

  /**
   * Un clic sur une vignette : sélection EN ATTENTE, et RIEN D'AUTRE.
   *
   * ⚠️ CHANGEMENT DE COMPORTEMENT CENTRAL : cliquer une silhouette n'écrit plus dans le
   * stockage et n'émet plus `mx:vehicle-changed` — seul `validerChoix()` le fait. La
   * sélection se marque par le cadre de la vignette (voir mx-vehicle.css,
   * `.mx-vpt:has(input:checked)::after`), comme partout ailleurs dans le thème ; le
   * bouton « Valider » s'active, ce qui lui tient lieu de confirmation visuelle.
   *
   * `handle` peut être `null` (aucune sélection en attente, cascade réinitialisée) : le
   * bouton se désactive et retrouve son `title` explicatif.
   *
   * ⚠️ Aucune ligne d'aide visible dans le balisage (voir mx-vehicle-picker.liquid) : un
   * lecteur d'écran annonce « bouton désactivé » sans jamais dire pourquoi, d'où ce
   * `title` posé UNIQUEMENT tant que le bouton l'est — le libellé visible (« Valider »)
   * suffit seul comme nom accessible une fois actif.
   */
  marquer(handle) {
    this.enAttente = handle || null;
    if (this.valider) {
      this.valider.disabled = !this.enAttente;
      this.valider.title = this.enAttente ? '' : this.t.aide || '';
    }
  }

  /**
   * Le bouton « Valider ». Seul point d'écriture du sélecteur : c'est ici, et ici
   * seulement, que le stockage est écrit et que `mx:vehicle-changed` part.
   *
   * `interne` neutralise notre propre écouteur de synchronisation le temps de l'émission :
   * sans lui, l'événement nous reviendrait et `peindre()` s'exécuterait une seconde fois
   * pour rien.
   *
   * ⚠️ La fermeture du tiroir NE PEUT PAS dépendre de `mx:vehicle-changed` : `definir()`
   * porte une garde anti-rebond (`valide === courant`) qui sort avant d'émettre quand la
   * machine validée est déjà celle en mémoire. Se caler sur l'événement pour fermer
   * laissait donc le tiroir ouvert quand le client revalidait sans rien changer — le
   * client a pourtant bien répondu à la question posée. Ici on ferme le `<dialog>`
   * directement, dans tous les cas, indépendamment de ce que `definir()` a émis ou non.
   */
  validerChoix() {
    if (!this.enAttente) return; // bouton désactivé dans ce cas ; garde-fou si forcé
    this.interne = true;
    definir(this.enAttente);
    this.interne = false;
    if (this.dansTiroir()) {
      // `rouvrir()` repeint le formulaire pour la prochaine ouverture (voir `peindre()`) ;
      // la fermeture elle-même ne passe plus par le tiroir qui écoute l'événement, mais
      // par le `<dialog>` trouvé au même titre que `dansTiroir()` le fait déjà.
      this.rouvrir();
      const dlg = this.closest('dialog');
      if (dlg) dlg.close();
    } else {
      this.montrerRecap();
    }
  }

  /**
   * « Retirer ma machine » : seule commande d'oubli du sélecteur. `definir(null)` vide le
   * stockage et émet l'événement avec `gabarit: null` — tous les blocs abonnés (pastille,
   * fiche produit) repassent à « aucune machine déclarée » sans rechargement. Le
   * formulaire revient ensuite à son état initial, listes vides, comme au premier
   * chargement.
   */
  oublierMachine() {
    this.interne = true;
    definir(null);
    this.interne = false;
    this.selects.univers.value = '';
    this.reinitialiser('marque');
    this.montrerFormulaire();
  }

  /**
   * Question posée au DOM, comme `chargementImage()` : pas un second drapeau, le même
   * `closest('dialog')` qui distingue déjà le tiroir de la page pour le chargement des
   * images.
   */
  dansTiroir() {
    return !!this.closest('dialog');
  }

  /**
   * Peinture d'ARRIVÉE.
   *
   * Sur la PAGE, un véhicule déjà mémorisé s'affiche en récapitulatif seul — c'est le
   * seul endroit qui rappelle la machine hors du tiroir.
   *
   * Dans le TIROIR, l'état validé n'existe PAS : on rouvre directement sur le formulaire
   * pré-rempli (`rouvrir()`), machine mémorisée ou non — le bandeau de compatibilité vient
   * déjà de dire quelle machine est mémorisée, c'est précisément depuis lui qu'on a ouvert
   * ce tiroir pour en changer, jamais pour la consulter.
   */
  peindre() {
    if (this.dansTiroir()) {
      if (courant && this.modeleDuGabarit(courant)) this.rouvrir();
      else this.montrerFormulaire();
      return;
    }
    if (courant && this.modeleDuGabarit(courant)) this.montrerRecap();
    else this.montrerFormulaire();
  }

  montrerFormulaire() {
    this.zoneForm.hidden = false;
    this.done.hidden = true;
    this.majOublier();
  }

  /** « Retirer ma machine » n'a de sens QUE si une machine est actuellement mémorisée. */
  majOublier() {
    if (this.oublier) this.oublier.hidden = !courant;
  }

  /**
   * « Changer » : rouvre le formulaire SANS effacer le véhicule mémorisé — seul
   * `validerChoix()` écrit, donc rouvrir puis quitter la page laisse le véhicule d'origine
   * intact. Les trois listes sont PRÉ-REMPLIES avec l'univers, la marque et le modèle du
   * véhicule courant, et sa silhouette est marquée comme sélection en attente : revalider
   * sans rien changer doit fonctionner directement.
   */
  rouvrir() {
    const modele = this.modeleDuGabarit(courant);
    this.done.hidden = true;

    if (!modele) {
      // Garde-fou : ne devrait pas arriver, `peindre()` n'affiche `done` que si résolu.
      this.selects.univers.value = '';
      this.reinitialiser('marque');
      this.montrerFormulaire();
      return;
    }

    this.selects.univers.value = modele.univers;
    this.remplir(
      this.selects.marque,
      this.listeMarques(modele.univers).map((m) => ({ value: m, label: m }))
    );
    this.selects.marque.value = modele.marque;
    this.etapes.marque.hidden = false;

    const modeles = this.listeModeles(modele.univers, modele.marque);
    this.remplir(
      this.selects.modele,
      modeles.map((m) => ({ value: m.handle, label: m.label }))
    );
    this.selects.modele.value = modele.handle;
    this.etapes.modele.hidden = false;

    this.remplirGabarits(modele, courant);
    this.montrerFormulaire();
  }

  /** État validé : la silhouette devient le résultat, plus le sujet. */
  montrerRecap() {
    const modele = this.modeleDuGabarit(courant);
    const gabarit = this.gabaritParHandle(courant);

    const donemedia = this.querySelector('[data-mx-donemedia]');
    if (donemedia) {
      vider(donemedia);
      if (gabarit && gabarit.image) {
        const img = document.createElement('img');
        img.className = 'mx-vp__donemedia-img';
        img.src = gabarit.image;
        img.alt = '';
        img.loading = this.chargementImage();
        img.decoding = 'async';
        img.addEventListener('error', () => this.poserPlaceholder(donemedia), { once: true });
        donemedia.appendChild(img);
      } else {
        this.poserPlaceholder(donemedia);
      }
    }

    const nomModele = this.querySelector('[data-mx-donemodele]');
    if (nomModele) nomModele.textContent = (modele && modele.label) || '';

    const note = this.querySelector('[data-mx-donenote]');
    if (note) {
      // Le libellé du gabarit vient du RÉFÉRENTIEL COMPLET (`this.data`), pas du
      // dictionnaire : les deux portent la même valeur traduite, mais celui-ci évite une
      // dépendance de plus et donne accès à la silhouette au même endroit.
      const texte = gabarit && gabarit.label;
      note.textContent = texte || '';
      note.hidden = !texte;
    }

    const voir = this.querySelector('[data-mx-see]');
    if (voir) {
      const url = modele && modele.collectionUrl;
      // Pas de collection résolvable => pas d'action, jamais un lien mort. Le cas couvre
      // aussi bien un modèle sans collection rattachée qu'une collection non publiée :
      // dans les deux cas Liquid n'a pas d'adresse à donner.
      if (url) {
        voir.href = url;
        voir.textContent = this.t.voir || '';
        voir.hidden = false;
      } else {
        voir.removeAttribute('href');
        voir.hidden = true;
      }
    }

    this.zoneForm.hidden = true;
    this.done.hidden = false;
  }

  /** Un autre sélecteur (ou le tiroir) a changé le véhicule : on se remet d'accord. */
  onSync() {
    if (!this.pret || this.interne) return;
    this.peindre();
  }
}

/* ===================================================================== PASTILLE */

/**
 * La pastille : rappelle la machine mémorisée, et rien d'autre. N'ouvre aucun tiroir
 * elle-même — voir `MxVehicleDrawer` plus bas, seule à en porter un désormais.
 */
class MxVehicleChip extends HTMLElement {
  connectedCallback() {
    const d = dict();
    if (!d) return;

    this.t = textes();
    this.nomEl = this.querySelector('[data-mx-chip-name]');
    this.bouton = this.querySelector('[data-mx-change]');

    this.onSync = this.onSync.bind(this);

    this.poserTextes();

    // Même contrat que le bouton statique du bandeau de compatibilité
    // (`assets/mx-compat.js`, `ouvrirAuClic`) : émettre `mx:vehicle-open` sans savoir qui
    // écoute. C'est `MxVehicleDrawer` qui répond.
    if (this.bouton) {
      this.bouton.addEventListener('click', () => {
        document.dispatchEvent(new CustomEvent(OPEN_EVENT));
      });
    }

    document.addEventListener(EVENT, this.onSync);
    this.peindre();
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onSync);
  }

  poserTextes() {
    this.querySelectorAll('[data-mx-t]').forEach((el) => {
      const cle = el.getAttribute('data-mx-t');
      if (this.t[cle]) el.textContent = this.t[cle];
    });
  }

  peindre() {
    const d = dict();
    const entree = courant && d ? d.gabarits[courant] : null;
    // Pas de véhicule mémorisé => la pastille n'existe pas. Elle ne rappelle rien.
    this.hidden = !entree;
    if (entree && this.nomEl) this.nomEl.textContent = entree.label;
  }

  onSync() {
    this.peindre();
  }
}

/* ======================================================================= TIROIR */

/**
 * Le tiroir du sélecteur : un `<dialog>` natif, TOUJOURS rendu (`mx-vehicle-lookup`, pied
 * de page) — jamais masqué, donc jamais besoin d'être « réveillé » pour s'ouvrir. Voir
 * `snippets/mx-vehicle-drawer.liquid` pour le pourquoi de cet emplacement.
 */
class MxVehicleDrawer extends HTMLElement {
  connectedCallback() {
    const d = dict();
    if (!d) return;

    this.t = textes();
    this.drawer = this.querySelector('[data-mx-drawer]');
    this.zone = this.querySelector('[data-mx-slot]');
    this.pied = this.querySelector('[data-mx-foot]');
    this.etat = this.querySelector('[data-mx-drawer-state]');
    this.charge = false;

    this.onSync = this.onSync.bind(this);
    this.onDemande = this.onDemande.bind(this);

    this.poserTextes();

    const fermer = this.querySelector('[data-mx-close]');
    if (fermer && this.drawer) fermer.addEventListener('click', () => this.drawer.close());

    document.addEventListener(EVENT, this.onSync);
    document.addEventListener(OPEN_EVENT, this.onDemande);
  }

  disconnectedCallback() {
    document.removeEventListener(EVENT, this.onSync);
    document.removeEventListener(OPEN_EVENT, this.onDemande);
  }

  poserTextes() {
    this.querySelectorAll('[data-mx-t]').forEach((el) => {
      const cle = el.getAttribute('data-mx-t');
      if (this.t[cle]) el.textContent = this.t[cle];
    });
  }

  /** Ouverture demandée par un autre bloc (pastille, bandeau de compatibilité). */
  onDemande() {
    if (!this.drawer) return;
    try {
      this.drawer.showModal();
    } catch (e) {
      this.drawer.setAttribute('open', ''); // très vieux navigateurs : tiroir non modal
    }
    // Récupéré au PREMIER appui seulement, puis gardé pour la durée de la page.
    if (!this.charge) this.recuperer();
  }

  onSync() {
    // Un choix fait DANS le tiroir (ou ailleurs pendant qu'il est ouvert) le referme : le
    // client a répondu à la question posée.
    if (this.drawer && this.drawer.open && courant) this.drawer.close();
  }

  /* ------------------------------------------------- le sélecteur à la demande */

  /**
   * Récupère le sélecteur par la Section Rendering API.
   *
   * L'URL SOURCE vient du dictionnaire (`mx-vehicle-lookup`, réglage `picker_url`),
   * déjà préfixée par la langue via `mx-url` côté Liquid. Elle n'est pas déduite de
   * `routes.root_url` ici : dépendre implicitement de la page d'accueil casserait le
   * tiroir le jour où le sélecteur est déplacé ailleurs — c'est le dictionnaire qui porte
   * ce choix explicite, pas ce module.
   *
   * Pas de réessai automatique : un échec ici est presque toujours durable (section
   * retirée, réseau coupé), et réessayer en boucle masquerait le message au client.
   */
  recuperer() {
    this.charge = true;
    const d = dict();
    const base = (d && d.url) || '/';
    const url = base + (base.indexOf('?') === -1 ? '?' : '&') + 'section_id=' + SECTION;

    this.etatAttente();

    fetch(url, { headers: { Accept: 'text/html' } })
      .then((r) => {
        if (!r.ok) throw new Error(String(r.status));
        return r.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        // On extrait l'ÉLÉMENT, pas la section : le wrapper porte la grille de page du
        // thème, qui n'a aucun sens dans un tiroir.
        const el = doc.querySelector('mx-vehicle-picker');
        if (!el) throw new Error('vide');
        if (this.etat) this.etat.hidden = true;
        vider(this.zone);
        const importe = document.importNode(el, true);
        this.zone.appendChild(importe);
        // PIED FIXE : troisième zone de la colonne flex du tiroir, pas un `position:
        // sticky` dans le corps qui défile (voir `.mx-vc__foot`, mx-vehicle.css). Le
        // balisage du sélecteur ne connaît pas le tiroir : on déplace le groupe
        // d'actions APRÈS import, une seule fois. `vider()` d'abord au cas où cette
        // récupération se reproduirait un jour (elle ne le fait pas aujourd'hui,
        // `this.charge` la verrouille) : jamais deux pieds superposés.
        if (this.pied) {
          vider(this.pied);
          const actions = importe.querySelector('.mx-vp__actions');
          const zoneForm = importe.querySelector('[data-mx-form]');
          if (actions) {
            this.pied.appendChild(actions);
            // Déplacé hors de `.mx-vp__form`, le groupe d'actions ne suit plus le
            // masquage de ce dernier en JS. Le tiroir ne montre plus jamais l'état VALIDÉ
            // (voir `peindre()` : `dansTiroir()` route toujours sur `rouvrir()`), donc
            // `zoneForm` n'y est plus jamais masqué et ce miroir garde simplement le pied
            // affiché en permanence — gardé comme filet si ce renvoi venait à changer.
            // `hidden` sur `.mx-vc__foot` profite de la même règle de cascade que
            // `.mx-vp [hidden]` (mx-vehicle.css, en tête de fichier), qui couvre aussi
            // `mx-vehicle-drawer [hidden]`.
            if (zoneForm) {
              const suivre = () => {
                this.pied.hidden = zoneForm.hidden;
              };
              suivre();
              new MutationObserver(suivre).observe(zoneForm, {
                attributes: true,
                attributeFilter: ['hidden'],
              });
            }
          }
        }
      })
      .catch(() => {
        this.etatEchec();
      });
  }

  etatAttente() {
    if (!this.etat) return;
    this.etat.hidden = false;
    vider(this.etat);
    const p = document.createElement('p');
    p.className = 'mx-vc__wait';
    p.textContent = this.t.chargement || '';
    this.etat.appendChild(p);
  }

  /** Message sobre + un lien vers la page qui porte le sélecteur. */
  etatEchec() {
    if (!this.etat) return;
    this.etat.hidden = false;
    vider(this.etat);
    const p = document.createElement('p');
    p.className = 'mx-vc__error';
    p.textContent = this.t.erreur || '';
    this.etat.appendChild(p);

    const d = dict();
    const base = d && d.url;
    if (base) {
      const a = document.createElement('a');
      a.className = 'mx-vc__errorlink';
      a.href = base;
      a.textContent = this.t.erreurLien || base;
      this.etat.appendChild(a);
    }
  }
}

/* ==================================================================== amorçage */

amorcer();

if (!customElements.get('mx-vehicle-picker')) {
  customElements.define('mx-vehicle-picker', MxVehiclePicker);
}
if (!customElements.get('mx-vehicle-chip')) {
  customElements.define('mx-vehicle-chip', MxVehicleChip);
}
if (!customElements.get('mx-vehicle-drawer')) {
  customElements.define('mx-vehicle-drawer', MxVehicleDrawer);
}

/**
 * Émission unique au chargement, pour que les blocs abonnés se peignent sans attendre une
 * interaction.
 *
 * ⚠️ PAS `requestAnimationFrame` — mesuré : dans un onglet qui n'est pas rendu (ouvert en
 * arrière-plan, fenêtre masquée, session restaurée), le rappel n'est JAMAIS appelé, donc
 * l'événement ne partait pas du tout. Le symptôme est traître : tout fonctionne à l'écran
 * pendant les tests et rien ne part chez un visiteur qui ouvre la page dans un onglet de
 * fond, sans la moindre erreur.
 *
 * `DOMContentLoaded` tient la même promesse sans dépendre du rendu : il se déclenche APRÈS
 * l'exécution de TOUS les scripts différés — dont le nôtre — donc après que les autres
 * blocs ont posé leurs écouteurs. Émettre en synchrone ici partirait trop tôt, et l'ordre
 * de chargement déciderait de qui reçoit l'événement.
 *
 * Le module étant lui-même différé, `readyState` vaut `interactive` à ce point ; `complete`
 * n'arrive que si le script est inséré après coup, et on repasse alors par la file des
 * tâches pour rester asynchrone dans les deux cas.
 */
if (courant) {
  if (document.readyState === 'complete') setTimeout(emettre, 0);
  else document.addEventListener('DOMContentLoaded', () => emettre(), { once: true });
}
